import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { VaultItem } from '@heybesafe/shared';
import { v4 as uuidv4 } from 'uuid';
import { useSafe } from '../../context/SafeContext';
import { useFamilyCategories } from '../../hooks/useFamilyCategories';
import { shareItemWithFamily } from '../../api/client';
import { base64UrlToBytes } from '../../crypto/base64url';
import { prepareShareForFamily } from '../../components/prepareShareForFamily';
import { familyKeyAuthentic } from '../../pkastore/familyKeyAuth';
import { HbsInput } from '../../components/hbs/Input';
import { HbsButton } from '../../components/hbs/Button';

/**
 * Create or rename a family list (collaborative checklist). Creating a list asks
 * only for a title + the owning category (from the route) — items are added in
 * the list detail view, where any family member can add/remove/check them. A new
 * list is written as a shared `ORG#` item via the same `share-item-v1` ECIES path
 * as a document (categoryId inside the ciphertext, circleId denormalized onto the
 * envelope). Mirrors DocumentEditorPage; lists have no body/links/attachments.
 */
export default function ListEditorPage(): JSX.Element {
  const { orgId, categoryId, itemId } = useParams<{ orgId: string; categoryId: string; itemId?: string }>();
  const navigate = useNavigate();
  const { orgs, sync, updateItem, items } = useSafe();
  const categoriesByOrg = useFamilyCategories();

  const org = orgs.find((o) => o.id === orgId);
  const category = (orgId ? categoriesByOrg.get(orgId) ?? [] : []).find((c) => c.categoryId === categoryId);

  const existing = itemId
    ? items.find((i) => i.id === itemId && i.orgId === orgId && i.type === 'list')
    : undefined;
  const isEdit = !!existing;

  const [title, setTitle] = useState(existing?.name ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const backTo = `/orgs/${orgId}/binder/${categoryId}`;

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!title.trim() || !orgId || !categoryId) return;
    if (!org?.publicKey) {
      setError('Family is missing its public key. Try refreshing.');
      return;
    }
    // PKA: authenticate the family public key before wrapping anything to it.
    if (!familyKeyAuthentic(org)) {
      setError("Could not verify this family's security key — do not save. Ask a family admin to sign in to the latest version.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (isEdit && existing) {
        // Rename only — never touch listItems (they merge per-entry elsewhere).
        await updateItem(existing.id, { type: 'list', name: title.trim() });
        await sync();
        navigate(`/orgs/${orgId}/binder/${categoryId}/lists/${existing.id}`);
        return;
      }

      const listId = uuidv4();
      const pubKey = base64UrlToBytes(org.publicKey);
      const list: VaultItem = {
        id: listId, type: 'list', name: title.trim(), categoryId,
        listItems: [],
        sensitive: false, favorite: false, tags: [],
        createdAt: '', updatedAt: '',
      };
      const encrypted = await prepareShareForFamily(list, pubKey, null, orgId, { itemId: listId });
      const itemToShare = category?.circleId ? { ...encrypted, circleId: category.circleId } : encrypted;
      const result = await shareItemWithFamily(itemToShare);
      if (!result.ok) {
        setError('Could not save the list. Please try again.');
        return;
      }
      await sync();
      navigate(`/orgs/${orgId}/binder/${categoryId}/lists/${listId}`);
    } catch (err) {
      console.error('ListEditorPage: save failed', err);
      setError('Could not save the list. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-full flex flex-col max-w-3xl">
      <form id="list-editor-form" onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-7 flex flex-col gap-4">
        <div className="mb-2">
          <p className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
            {org?.name ?? 'Family'}{category ? ` · ${category.name}` : ''}
          </p>
          <h1 className="font-display font-semibold text-[24px] tracking-hbs-tight text-hbs-text">
            {isEdit ? 'Rename list' : 'New list'}
          </h1>
        </div>

        <HbsInput label="Title" value={title} onChange={setTitle} placeholder="e.g. Groceries" required autoFocus name="title" />

        {error && <p role="alert" className="text-[12px] text-hbs-warning">{error}</p>}
      </form>
      <div className="shrink-0 border-t border-hbs-border bg-hbs-bg px-7 py-4 flex gap-2">
        <HbsButton id="list-save-button" type="submit" form="list-editor-form" loading={loading} fullWidth>Save</HbsButton>
        <HbsButton type="button" variant="ghost" fullWidth id="list-cancel-button" onClick={() => navigate(backTo)}>Cancel</HbsButton>
      </div>
    </div>
  );
}
